import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoveMoneyContext } from '../../context/MoveMoneyContext';
import '../MoveMoney/MoveMoney.css';
import { confirmMoneyMovement } from '../../api';

interface Beneficiary {
  receiverId: string;
  receiverFirstName: string;
  receiverLastName: string;
  receiverAccountNumber: string;
  receiverBank: string;
  receiverCurrency: string;
  receiverEmail: string;
  receiverCountry: string;
}

interface AccountInfo {
  accountName: string;
  accountNumber: string;
  balance: number;
  currency: string;
  core: string;
}

const Confirmation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [beneficiaryDetails, setBeneficiaryDetails] = useState<Beneficiary | null>(null);
  const [accountDetails, setAccountDetails] = useState<AccountInfo | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const navigate = useNavigate();
  const moveMoneyContext = useContext(MoveMoneyContext);

  if (!moveMoneyContext) {
    throw new Error('MoveMoneyContext must be used within a MoveMoneyProvider');
  }

  const {
    transactionId,
    beneficiary,
    amount,
    fromAccount,
    paymentMethod,
    paymentMethodResponse,
    accounts,
    beneficiaries,
  } = moveMoneyContext;

  useEffect(() => {
    setLoadingDetails(true);
    try {
      const selectedBeneficiary = beneficiaries.find(b => b.receiverId === beneficiary);
      if (selectedBeneficiary) {
        setBeneficiaryDetails(selectedBeneficiary);
      }

      const selectedAccount = accounts.find(a => a.accountNumber === fromAccount);
      if (selectedAccount) {
        setAccountDetails(selectedAccount);
      }
    } catch (err) {
      console.error('Error loading confirmation details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [beneficiary, fromAccount, accounts, beneficiaries]);

  const handleConfirm = async () => {
    setIsLoading(true);

    if (!beneficiaryDetails || !accountDetails || !paymentMethodResponse) {
      alert("Missing transaction details. Cannot proceed.");
      setIsLoading(false);
      return;
    }

    try {
      const selectedMethod = paymentMethodResponse.paymentMethodList?.find(
        (m: any) => m.transferMethodCode === paymentMethod
      );

      const payload = {
        customerID: localStorage.getItem('currentUserId') || 'nova.brooks',
        eventMapInstanceId: paymentMethodResponse.eventMapInstanceID,
        sourceInstanceId: paymentMethodResponse.sourceInstanceID,
        externalTransferDetailsList: [
          {
            bankName: null,
            routingNumber: null,
            accountNumber: beneficiaryDetails.receiverAccountNumber,
            beneficiaryName: `${beneficiaryDetails.receiverFirstName} ${beneficiaryDetails.receiverLastName}`,
            beneficiaryEmail: beneficiaryDetails.receiverEmail,
            countryCode: beneficiaryDetails.receiverCountry || 'US',
            beneficiaryPhone: '1215610654',
            beneficiaryStreetName: 'Street577',
            beneficiaryRestricted: false,
            beneficiaryAddressLine1: '456',
            beneficiaryAddressLine2: '890',
            beneficiaryZIPCode: null,
            beneficiaryCity: 'New York',
            beneficiaryState: 'New York',
            paymentCurrency: beneficiaryDetails.receiverCurrency || 'USD',
            benificiaryOptionsSelected: [
              {
                externalReferenceId: '85a8bdc7e3c848e7a5fe31907e5d200b',
                transferMethodName: paymentMethod,
                transferAmount: parseFloat(amount),
                speed: selectedMethod?.speed || 'Instant'
              }
            ]
          }
        ],
        fraudDetectionRequest: {
          beneficiaryName: `${beneficiaryDetails.receiverFirstName} ${beneficiaryDetails.receiverLastName}`,
          transferAmount: amount
        },
        transferDetails: {
          accountNumber: accountDetails.accountNumber,
          amount: parseFloat(amount),
          core: accountDetails.core || 'finxact',
          currencyCode: accountDetails.currency || 'USD',
          transferDate: new Date().toISOString().split('T')[0]
        },
        isValidated: true
      };

      const response = await confirmMoneyMovement(payload);
      console.log('Confirm Money Movement Response:', response);
      setIsLoading(false);

      if (response.success) {
        const transferSummary = response.externalTransferResponse?.externalTransferSummary?.[0];

        const message = transferSummary
          ? `✅ Payment ${transferSummary.status}!\n\nTransaction ID: ${transferSummary.transactionId}\nAmount: ${transferSummary.currencyCode} ${transferSummary.targetAmount}\nRecipient: ${transferSummary.counterPartyName}\nMethod: ${transferSummary.transferMethod}`
          : `✅ Payment successful!`;

        alert(message);
        navigate('/accounts');
      } else {
        alert('❌ There was an error processing your transaction. Please try again.');
      }

    } catch (err) {
      console.error('Error confirming money movement (Step 5):', err);
      alert('Failed to confirm transaction.');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/move-money/payment-methods');
  };

  const formatCurrency = (amount: string | number, currency: string = 'USD') => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const getPaymentMethodDetails = () => {
    if (paymentMethodResponse?.paymentMethodList) {
      const selectedMethod = paymentMethodResponse.paymentMethodList.find(
        (method: any) => method.transferMethodCode === paymentMethod
      );
      if (selectedMethod) {
        return {
          name: selectedMethod.transferMethodCodeDescription || selectedMethod.transferMethodCode,
          speed: selectedMethod.speed,
          fees: selectedMethod.transferFees + selectedMethod.processingFee,
          limit: selectedMethod.paymentMethodLimit,
          effectiveDays: selectedMethod.paymentEffectiveInDays
        };
      }
    }
    return null;
  };

  if (loadingDetails) {
    return (
      <div className="flow-step">
        <h2 className="step-header">Confirmation</h2>
        <div className="loading">Loading transaction details...</div>
      </div>
    );
  }

  const paymentMethodDetails = getPaymentMethodDetails();

  return (
    <div className="flow-step">
      <h2 className="step-header">Confirmation</h2>
      <div className="confirmation-summary">
        <h3>Transfer Details</h3>

        <div className="confirmation-section">
          <h4>To:</h4>
          {beneficiaryDetails ? (
            <div className="beneficiary-summary">
              <p><strong>Name:</strong> {beneficiaryDetails.receiverFirstName} {beneficiaryDetails.receiverLastName}</p>
              {/* <p><strong>Bank:</strong> {beneficiaryDetails.receiverBank || 'N/A'}</p> */}
              <p><strong>Account:</strong> ****{beneficiaryDetails.receiverAccountNumber.slice(-4)}</p>
              <p><strong>Email:</strong> {beneficiaryDetails.receiverEmail}</p>
            </div>
          ) : (
            <p>Beneficiary details not available</p>
          )}
        </div>

        <div className="confirmation-section">
          <h4>From:</h4>
          {accountDetails ? (
            <div className="account-summary">
              <p><strong>Account:</strong> {accountDetails.accountName}</p>
              <p><strong>Account Number:</strong> ****{accountDetails.accountNumber.slice(-4)}</p>
              <p><strong>Currency:</strong> {accountDetails.currency}</p>
              <p><strong>Current Balance:</strong> {formatCurrency(accountDetails.balance, accountDetails.currency)}</p>
            </div>
          ) : (
            <p>Account details not available</p>
          )}
        </div>

        <div className="confirmation-section">
          <h4>Transfer:</h4>
          <p><strong>Amount:</strong> {formatCurrency(amount)}</p>
          <p><strong>Payment Method:</strong> {paymentMethodDetails?.name || paymentMethod}</p>
          {paymentMethodDetails && (
            <>
              <p><strong>Speed:</strong> {paymentMethodDetails.speed}</p>
              <p><strong>Fees:</strong> {formatCurrency(paymentMethodDetails.fees)}</p>
              <p><strong>Limit:</strong> {formatCurrency(paymentMethodDetails.limit)}</p>
              <p><strong>Effective In:</strong> {paymentMethodDetails.effectiveDays} day(s)</p>
            </>
          )}
        </div>
      </div>
      <div className="navigation-buttons">
        <button onClick={handleBack} disabled={isLoading}>Back</button>
        <button onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default Confirmation;
